import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { isEqual } from "lodash";
import PropTypes from "prop-types";

import DownloadCSVModal from "components/DownloadCSVModal";
import FacetedSearchBar from "components/FacetedSearchBar";
import FeedTable from "components/FeedTable";
import SetView from "components/SetView";
import SPLink from "components/SPLink";
import {
	SUBAWARDS_BASE_ROUTE,
	COLUMN_HEADER_KEYS,
	MODIFICATION_DRAFT,
} from "globals/constants";
import {
	useDictionary,
	usePagination,
	useSelectedRows,
	useColumnHeaders,
	useRequestChain,
	useActiveColumns,
	useDownloadCsv
} from "hooks";
import {
	findSubawards,
	setPaging,
} from "store/actions/subawardList";
import { subawardListSelectors } from "store/selectors";
import { exportReportAsCSV, generateDatedDownloadFileName, getKeysFromColumnHeaders, notifyError } from "utils";

import styles from "./styles.css";


const SubawardListView = (
	{ subawardsListId,
		searchFacets,
		onSearchChange,
		availableColumns,
		actualFacets,
		//TODO: remove it when BE is ready
		defaultColumnHeaders
	}) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = usePagination("subawards", setPaging);
	const [ isSetViewOpen, setIsSetViewOpen ] = useState(false);
	const [ isDownloadOpen, setIsDownloadOpen ] = useState(false);
	const [ rows, setRows ] = useState([]);
	const [ previousSearch, setPreviousSearch ] = useState({});
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection} = useSelectedRows({ rows, getRowKey });

	const facetFilterRequestChain = useRequestChain();

	const dictionary = useDictionary("subawards");

	const { isDownloading, download } = useDownloadCsv();

	const {
		items,
		totalItems,
		search,
		isFetching: isLoading,
		isRefresh,
	} = useSelector(subawardListSelectors.content);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.SUBAWARD);

	const { columns: activeColumns, updateActiveColumns } = useActiveColumns(COLUMN_HEADER_KEYS.SUBAWARD, formColumnHeaders);

	const updatedActiveColumns = useMemo(
		() => activeColumns.map(
			activeColumn => formColumnHeaders.find(formColumnHeader => activeColumn.id === formColumnHeader.id)
		),
		[ activeColumns, formColumnHeaders ],
	);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const downloadCSV = async ({ selections: selectedColumnHeaders }) => {
		const filename = generateDatedDownloadFileName({
			prefix: dictionary.filePrefix || "SP_Subawards",
			extension: "csv"
		});

		await exportReportAsCSV({
			submissionType: "subaward",
			selectedColumnHeaders,
			filename,
			download,
			onComplete: () => setIsDownloadOpen(false),
			onError: notifyError
		});
	};

	const hasItems = isNonEmptyArray(items);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isNumberLinkable = isNonEmptyString(item.subawardId) && isNonEmptyString(item.subawardNumber);
				const linkedSubawardNumber = isNumberLinkable
					? <SPLink to={`${SUBAWARDS_BASE_ROUTE}/${item.subawardId}`}>{item.subawardNumber}</SPLink>
					: "";

				item.subRows.forEach(subRow => {
					if (subRow.modificationStatus === MODIFICATION_DRAFT) {
						subRow.subawardNumber = <span className={styles.draftSubawardNumber}>{subRow.subawardNumber} (Draft)</span>;
					}
				});

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					subawardNumber: linkedSubawardNumber
				};
			})
			: [];
		setRows(updatedRows);
	}, [ items, selectedRows, hasItems ]);

	useEffect(() => {
		if (!isEqual(search, previousSearch)) {
			setPreviousSearch(search);
			setPageNumber(1);
		}
	}, [ previousSearch, search, setPageNumber ]);

	useEffect(() => {
		if (activeColumns?.length > 0 && (!isEqual(previousSearch, search) || !isLoading)) {
			facetFilterRequestChain.addRequest(
				"refreshSubawards",
				() => {
					updateActiveColumns(updatedActiveColumns, false);

					return findSubawards({
						pageLimit,
						pageOffset,
						searchTerms: search,
						fieldKeys: requiredFieldKeys
					});
				});
		}
	}, [
		search,
		pageLimit,
		pageOffset,
		requiredFieldKeys,
		updatedActiveColumns
	 ]);

	return (
		<div id="list-subaward">
			<VerticalSpacing>
				<BasePadding>
					<PaddedCell className={styles.proposalListOptions}
						radius={{ top: true }}
						border={{ top: true, left: true, right: true }}
					>
						<div className={styles.proposalListSearch}>
							<FacetedSearchBar
								ariaLabel={dictionary.subawardsSearchAriaLabel || "Search subawards"}
								facetAriaControls={subawardsListId}
								facets={searchFacets}
								onChange={onSearchChange}
								actualFacets={actualFacets}
								type="SUBAWARD"
							/>
						</div>
						<div className={styles.proposalListActions}>

							<SetView
								isOpen={isSetViewOpen}
								options={availableColumns}
								selections={activeColumns}
								onOpen={() => setIsSetViewOpen(true)}
								onClose={() => setIsSetViewOpen(false)}
								onUpdate={updateActiveColumns}
								requireKeys={false}
							/>
							<DownloadCSVModal
								isOpen={isDownloadOpen}
								onClick={() => setIsDownloadOpen(!isDownloadOpen)}
								onCancel={() => setIsDownloadOpen(false)}
								onSave={downloadCSV}
								options={availableColumns}
								selections={activeColumns}
								isDownloading={isDownloading}
							/>
						</div>
					</PaddedCell>

					<div>
						<FeedTable
							isLoading={isLoading}
							tableid={subawardsListId}
							isRefresh={isRefresh}
							headers={activeColumns}
							rows={rows}
							totalItems={totalItems}
							pageLimit={pageLimit}
							pageOffset={pageOffset}
							onPageChange={setPageNumber}
							onPageLimitChange={setPageLimit}
							onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
							overflow="visible"
							isSelectable={false}
							isExpandable={true}
							isSelectAll={isSelectAll}
							accessibilityLabel={({ projectTitle, _item }) => (
								`${projectTitle}, ${dictionary.accessibilityLabelSubawardNumber || "Subaward #"}${_item.proposalNumber}`
							)}
							noResultsLabel={dictionary.noResults || "No Saved Subawards"}
							captionText="Subawards"
						/>
					</div>
				</BasePadding>
			</VerticalSpacing>
		</div>
	);
};

SubawardListView.propTypes = {
	subawardsListId: PropTypes.string
};

export default SubawardListView;
